/**
 * Google Analytics 4, gated on the visitor's analytics consent.
 *
 * GA4 direct rather than Google Tag Manager. GTM earns its indirection when there are several tags
 * to manage without redeploying; OfficeA11y has exactly one, so a container would add a second
 * vendor hop and a configuration surface living outside this repository. For a product sold on
 * accessibility compliance, a data flow a customer can read in the source is worth more than
 * flexibility nobody is using. (Meetabl uses GTM, which is a reasonable choice there and not one
 * this needs to copy.)
 *
 * **The contract this file has to keep.** `policies.ts` tells visitors, in the Privacy Policy and
 * again in the Security FAQ, that:
 *
 *   - no analytics script is loaded and no analytics cookie is set until they consent;
 *   - withdrawing consent *removes the analytics cookies* and stops further collection.
 *
 * Both sentences are promises this module makes true. The second is the one that is easy to get
 * wrong: unloading the script stops new collection but leaves `_ga` and `_ga_<STREAM>` sitting in
 * the browser, so a withdrawal that only removes the script would make a published policy false.
 * `removeAnalytics` deletes the cookies.
 */

/** Window shape including the GA4 runtime globals. */
interface GaWindow extends Window {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  [key: `ga-disable-${string}`]: boolean | undefined;
}

const getGaWindow = (): GaWindow => globalThis as unknown as GaWindow;

/** A GA4 measurement id, e.g. G-646JC6X5QB. Not a GTM container id, and not a property id. */
const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{8,12}$/;

const SCRIPT_SELECTOR = 'script[src*="googletagmanager.com/gtag/js"]';

/**
 * The configured measurement id, or null if absent or malformed.
 *
 * Returning null on a malformed value is deliberate: injecting a script with a junk id would
 * silently collect nothing while looking instrumented, which is the failure mode that left this
 * product with a live GA4 property receiving no data in the first place.
 */
export const getMeasurementId = (): string | null => {
  const id: unknown = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  if (typeof id !== 'string' || !MEASUREMENT_ID_PATTERN.test(id)) {
    return null;
  }
  return id;
};

/**
 * Consent Mode v2 signals for advertising. Denied by default and for as long as the visitor has not
 * turned on the `marketing` consent category; granted only while it is on. Analytics storage is
 * not in this list on purpose: this module loads gtag only after the analytics category is on.
 */
const AD_CONSENT_KEYS = ['ad_storage', 'ad_user_data', 'ad_personalization'] as const;

const adConsent = (value: 'granted' | 'denied'): Record<string, string> =>
  Object.fromEntries(AD_CONSENT_KEYS.map(key => [key, value]));

/** Reports whether the GA4 script is currently on the page. */
export const isAnalyticsLoaded = (): boolean => document.querySelector(SCRIPT_SELECTOR) !== null;

/**
 * Loads GA4 and starts collection.
 *
 * Call only after the visitor has consented to the analytics category. No-ops when no valid
 * measurement id is configured, or when the script is already present.
 */
export const loadAnalytics = (marketingAllowed = false): void => {
  const id = getMeasurementId();
  if (!id || isAnalyticsLoaded()) {
    return;
  }

  const w = getGaWindow();
  // A previous withdrawal in this session sets the opt-out flag; consenting again clears it.
  delete w[`ga-disable-${id}`];

  w.dataLayer = w.dataLayer ?? [];
  // gtag must push `arguments` itself — not a copied array — or GA drops the call.
  const gtag: GaWindow['gtag'] = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer?.push(arguments);
  };
  w.gtag = gtag;

  // Consent Mode v2: the defaults must be queued before `config`, or the first hit goes out under
  // the wrong state. Advertising signals start denied. They are raised below only when the visitor
  // has turned on the marketing category.
  gtag('consent', 'default', adConsent('denied'));
  if (marketingAllowed) {
    gtag('consent', 'update', adConsent('granted'));
  }

  gtag('js', new Date());
  gtag('config', id, {
    // The visitor reached this call by consenting, so analytics storage is granted. Google signals
    // and ad personalization stay off whatever the consent state: conversion measurement needs
    // neither, and both of these are real GA4 config parameters that turn those off.
    //
    // `anonymize_ip` is deliberately NOT set. It is a Universal Analytics parameter that GA4
    // ignores — GA4 masks IP addresses by default and offers no switch. Setting it would look
    // like a control that isn't there, and an earlier revision of this file had a test asserting
    // it, which pinned a value that did nothing. The Privacy Policy's statement that Google
    // truncates the IP before storage rests on GA4's documented default, not on this call.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.append(script);
};

/**
 * Removes GA4 and the cookies it set.
 *
 * Three things have to happen, and dropping any one leaves a published promise unkept:
 *   1. the script goes, so nothing new is sent;
 *   2. `ga-disable-<ID>` is set, which stops any already-evaluated gtag from sending;
 *   3. the `_ga` cookies are deleted, because "withdrawing consent removes the analytics cookies"
 *      is a sentence in the Privacy Policy.
 */
export const removeAnalytics = (): void => {
  const id = getMeasurementId();

  for (const script of document.querySelectorAll(SCRIPT_SELECTOR)) {
    script.remove();
  }

  const w = getGaWindow();
  if (id) {
    w[`ga-disable-${id}`] = true;
  }
  w.dataLayer = [];
  delete w.gtag;

  clearAnalyticsCookies();
};

/**
 * Deletes GA4's cookies: `_ga` and the per-stream `_ga_<STREAM>`.
 *
 * A cookie is only deleted by a Set-Cookie whose name, domain AND path match the original. GA sets
 * its cookies on the registrable domain, so an expiry sent for the exact host alone leaves the
 * `.example.com` copy in place. Both are attempted, along with every parent domain, because the
 * cheap over-broad attempt is harmless and the narrow one is what silently fails.
 *
 * `unicorn/no-document-cookie` is disabled here deliberately, and this is the only place in `src`
 * that touches `document.cookie`. The rule's replacement, the asynchronous CookieStore API, cannot
 * do this job: it is absent from Safari and from jsdom, so the code would be untestable and would
 * silently no-op for a large share of visitors — on the one path whose failure makes a published
 * privacy policy false. Deleting another origin's cookie across domain variants is precisely what
 * `document.cookie` is for.
 */
export const clearAnalyticsCookies = (): void => {
  const names = document.cookie
    .split(';')
    .map(c => c.split('=')[0]?.trim())
    .filter((n): n is string => !!n && (n === '_ga' || n.startsWith('_ga_')));

  if (names.length === 0) {
    return;
  }

  const host = globalThis.location.hostname;
  const parts = host.split('.');
  const domains = [undefined, host, ...parts.map((_, i) => `.${parts.slice(i).join('.')}`)];

  for (const name of names) {
    for (const domain of domains) {
      // eslint-disable-next-line unicorn/no-document-cookie -- as above.
      document.cookie =
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/` +
        (domain ? `; domain=${domain}` : '');
    }
  }
};

/**
 * Bring the page into line with a consent decision. Idempotent, and safe to call on every load.
 *
 * This is the only function callers should need: pass the value of the visitor's `analytics`
 * consent category and it loads or unloads accordingly.
 */
export const syncAnalyticsConsent = (analyticsAllowed: boolean, marketingAllowed = false): void => {
  if (analyticsAllowed) {
    const alreadyLoaded = isAnalyticsLoaded();
    loadAnalytics(marketingAllowed);
    // Already loaded: the script is not re-injected, so a change to the marketing category has to
    // reach gtag as a consent update. A fresh load applied it itself.
    if (alreadyLoaded) {
      updateAdConsent(marketingAllowed);
    }
  } else {
    removeAnalytics();
  }
};

/**
 * Raises or lowers the Consent Mode advertising signals on a gtag that is already running.
 * No-op when gtag is not on the page, so it can be called on every consent decision.
 */
export const updateAdConsent = (marketingAllowed: boolean): void => {
  const w = getGaWindow();
  if (typeof w.gtag !== 'function') {
    return;
  }
  w.gtag('consent', 'update', adConsent(marketingAllowed ? 'granted' : 'denied'));
};
